import { useEffect, useState, type ComponentType } from 'react'
import {
  AlertCircle,
  Bot,
  CheckCircle2,
  Flag,
  Loader2,
  RotateCcw,
  Tag,
  User,
} from 'lucide-react'
import type {
  SystemIssueMessage,
  SystemIssueMessageIcon,
  SystemIssuePriority,
  SystemIssueStatus,
} from '../../types/system-issues'
import { systemIssuesService } from '../../../../services/systemIssuesService'
import { linkifyText } from '../../../occurrences/utils/linkify'
import { formatDateTime } from '../../../occurrences/utils/date-format'
import { ImageLightbox } from '../../../../components/chat/ImageLightbox'
import { MessageCard } from '../../../occurrences/components/chat/messages/MessageCard'
import {
  EventLine,
  Strong,
  type EventAccent,
} from '../../../occurrences/components/chat/messages/TimelineEvents'
import { PRIORITY_LABELS, STATUS_LABELS } from '../shared/IssueRow'

const SYSTEM_ICONS: Record<
  SystemIssueMessageIcon,
  ComponentType<{ className?: string }>
> = {
  bot: Bot,
  alert: AlertCircle,
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface AttachmentThumbnailProps {
  messageId: number
  index: number
  attachment: { name: string; size: number; type: string; url?: string }
}

/**
 * Anexos aqui são sempre imagens — é o único tipo que o upload aceita (ver
 * `SystemIssuesUploadsService.prepareUpload`) — por isso já resolve a URL
 * assinada ao montar (em vez de só no clique, como o download genérico de
 * Ocorrências) e mostra uma miniatura. Clicar abre em tamanho grande via
 * `ImageLightbox`, reaproveitando a mesma URL já resolvida.
 */
function AttachmentThumbnail({
  messageId,
  index,
  attachment,
}: AttachmentThumbnailProps) {
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(
    attachment.url ?? null
  )
  const [failed, setFailed] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  useEffect(() => {
    if (resolvedUrl) return
    let cancelled = false
    systemIssuesService
      .getAttachmentDownloadUrl(messageId, index)
      .then((url) => {
        if (!cancelled) setResolvedUrl(url)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [messageId, index, resolvedUrl])

  if (failed) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
        title={attachment.name}
      >
        <AlertCircle className="h-3.5 w-3.5" />
        Falha ao carregar {attachment.name}
      </span>
    )
  }

  if (!resolvedUrl) {
    return (
      <div className="flex h-16 w-16 items-center justify-center rounded-md border border-border-subtle bg-surface-muted">
        <Loader2 className="h-4 w-4 animate-spin text-subtle" aria-hidden />
      </div>
    )
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setLightboxOpen(true)}
        title={`Abrir ${attachment.name} (${formatBytes(attachment.size)})`}
        className="h-16 w-16 shrink-0 overflow-hidden rounded-md border border-border-subtle transition hover:border-blue-300"
      >
        <img
          src={resolvedUrl}
          alt={attachment.name}
          className="h-full w-full object-cover"
        />
      </button>
      {lightboxOpen && (
        <ImageLightbox
          src={resolvedUrl}
          alt={attachment.name}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  )
}

/**
 * Item da conversa do chamado: mensagem como card com avatar ("**Nome**
 * abriu o chamado · data" na abertura) — o mesmo desenho da conversa de
 * Ocorrências — e mudanças registradas pelo sistema como linha de evento.
 */
export function IssueMessageItem({
  message,
  isOpening = false,
  isOwn = false,
}: {
  message: SystemIssueMessage
  /** Primeira mensagem: a descrição de quem abriu o chamado. */
  isOpening?: boolean
  /** Mensagem de quem está olhando: à direita, em azul-claro, "Você". */
  isOwn?: boolean
}) {
  if (message.kind === 'system') return <IssueEventLine message={message} />

  const authorName = isOwn ? 'Você' : message.author?.full_name || 'Usuário'
  const when = formatDateTime(message.created_at)
  return (
    <MessageCard
      messageId={message.id}
      authorName={authorName}
      picture={message.author?.picture ?? null}
      header={
        isOpening
          ? `abriu o chamado · ${when}`
          : // Cargo de quem escreveu (como na conversa de Ocorrências).
            `${!isOwn && message.author?.role ? `· ${message.author.role} ` : ''}· ${when}`
      }
      own={isOwn}
    >
      {message.content && (
        <p className="mt-2.5 whitespace-pre-wrap break-words text-[15px] text-body">
          {linkifyText(message.content)}
        </p>
      )}
      {message.attachments && message.attachments.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {message.attachments.map((att, idx) => (
            <li key={`${message.id}-${idx}`}>
              <AttachmentThumbnail
                messageId={message.id}
                index={idx}
                attachment={att}
              />
            </li>
          ))}
        </ul>
      )}
    </MessageCard>
  )
}

interface ChangePayload {
  status_change?: { from: SystemIssueStatus; to: SystemIssueStatus }
  priority_change?: { from: SystemIssuePriority; to: SystemIssuePriority }
  assignment?: { assigned_to_id: number; assigned_to_name?: string }
}

/**
 * Mudança registrada pelo sistema como linha de evento (a data fica no
 * passar do mouse, como na tela de referência). Mensagens antigas ou de
 * outro tipo mostram o texto gravado.
 */
function IssueEventLine({ message }: { message: SystemIssueMessage }) {
  const p = (message.system_payload ?? {}) as ChangePayload
  const actor = message.author?.full_name
  const line = (
    icon: ComponentType<{ className?: string }>,
    body: React.ReactNode,
    accent: EventAccent
  ) => (
    <EventLine
      icon={icon}
      accent={accent}
      at={message.created_at}
      timeAsTooltip
      messageId={message.id}
    >
      {body}
    </EventLine>
  )

  if (p.assignment) {
    // Mensagens de antes do nome no payload: o nome vem do texto gravado.
    const nome =
      p.assignment.assigned_to_name ??
      message.content.replace(/^Atribuído a /, '')
    return line(
      User,
      <>
        Chamado atribuído a <Strong>{nome}</Strong>
      </>,
      'blue'
    )
  }
  if (p.status_change) {
    const { from, to } = p.status_change
    if (to === 'resolved') {
      return line(
        CheckCircle2,
        actor ? (
          <>
            Chamado resolvido por <Strong>{actor}</Strong>
          </>
        ) : (
          'Chamado resolvido'
        ),
        // Mesma cor do status "Resolvido" (selo e ponto).
        'teal'
      )
    }
    if (from === 'resolved') {
      return line(
        RotateCcw,
        actor ? (
          <>
            Chamado reaberto por <Strong>{actor}</Strong>
          </>
        ) : (
          'Chamado reaberto'
        ),
        'orange'
      )
    }
    return line(
      Tag,
      <>
        Status alterado de <Strong>{STATUS_LABELS[from]}</Strong> para{' '}
        <Strong>{STATUS_LABELS[to]}</Strong>
      </>,
      'violet'
    )
  }
  if (p.priority_change) {
    return line(
      Flag,
      <>
        Prioridade alterada de{' '}
        <Strong>{PRIORITY_LABELS[p.priority_change.from]}</Strong> para{' '}
        <Strong>{PRIORITY_LABELS[p.priority_change.to]}</Strong>
      </>,
      'amber'
    )
  }
  const Icon = message.system_icon ? SYSTEM_ICONS[message.system_icon] : Bot
  return line(
    Icon,
    <span className="whitespace-pre-wrap">{linkifyText(message.content)}</span>,
    'gray'
  )
}
